import { useMemo } from 'react'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['auditLogs']

type AuditLogFilters = {
  // Add any filters you need
}

type UseAuditLogsOptions = {
  filters?: AuditLogFilters,
  performHttpRequests?: boolean,
}

function useAuditLogs(options: UseAuditLogsOptions = {}) {
  const { performHttpRequests = true } = options

  const {
    updatedEntities: { auditLogs: auditLogsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { loading } = useReduxAction(
    'auditLogs',
    'loadAuditLogs',
    {},
    [performHttpRequests],
    {
      shouldPerformFn: (moduleState: any) => performHttpRequests && !moduleState.loading && !moduleState.loaded,
    },
  )

  const { auditLogs } = useSelector(reduxState => reduxState.entities)

  const transformedAuditLogs = useMemo(() => Object.values(auditLogs).map((log: any) => {
    // Transform the changes array into a readable string.
    // Mostly changes will be an array of changes, where each element is a hash of attribute, old_value, new_value
    // The second case, is for the creation of a new record,
    // and 'changes' has been coerced to provide a summary of the record attributes
    // lastly, if the record is deleted, changes is just a string 'Record Deleted'
    let changesText = ''
    if (log.changes && Array.isArray(log.changes)){
      changesText = log.changes.map((change: any) => {
        const { attribute, old_value, new_value } = change
        return `${attribute}: ${old_value} => ${new_value}`
      }).join('\n')
    } else if (log.changes && typeof log.changes === 'object'){
      changesText = Object.entries(log.changes).map(([key, value]) => `${key}: ${value}`).join('\n')
    } else if (log.changes === 'Record Deleted'){
      changesText = 'Record Deleted'
    }

    return {
      ...log,
      changes: changesText,
    }
  }).reverse(), [auditLogs, auditLogsUpdatedAt])

  const hasTransformedAuditLogs = transformedAuditLogs && transformedAuditLogs.length > 0

  return {
    hasTransformedAuditLogs,
    loading,
    transformedAuditLogs,
  }
}

export default useAuditLogs
